import * as Sentry from '@sentry/react'

import { DeviceHealthStatus } from './deviceHealth.types'

// Warning logic thresholds per PT-3633: a field only warns past these points,
// not simply on any imperfect value.
const BATTERY_HEALTH_SCORE_WARNING_THRESHOLD = 50
const STORAGE_FREE_PCT_WARNING_THRESHOLD = 10
const SYNC_WARNING_DAYS = 30
const MB_PER_GB = 1024
const DAYS_PER_MONTH = 30

const COMPLIANCE_STATE_TRANSLATION_IDS = {
  compliant: 'DeviceHealth_compliance_state_compliant',
  configmanager: 'DeviceHealth_compliance_state_config_manager',
  conflict: 'DeviceHealth_compliance_state_conflict',
  error: 'DeviceHealth_compliance_state_error',
  ingraceperiod: 'DeviceHealth_compliance_state_in_grace_period',
  noncompliant: 'DeviceHealth_compliance_state_noncompliant',
  unknown: 'DeviceHealth_compliance_state_unknown',
} as const

interface StorageFields {
  storageFreeMB?: number | null
  storageTotalMB?: number | null
}

interface BatteryFields {
  healthScore?: number | null
}

interface DeviceHealthSignals extends StorageFields {
  battery?: BatteryFields | null
  complianceState?: string | null
  isEncrypted?: boolean | null
  lastSyncedAt?: string | Date | null
}

/**
 * Normalizes "Non-compliant", "noncompliant", "NonCompliant", etc. to the
 * same key so they can be compared/looked up regardless of API casing.
 */
const normalizeComplianceState = (complianceState: string) =>
  complianceState.replaceAll(/[^a-z]/gi, '').toLowerCase()

const getStorageFreePctRaw = (deviceInfo?: StorageFields | null) => {
  const total = deviceInfo?.storageTotalMB
  const free = deviceInfo?.storageFreeMB

  if (!total || typeof free !== 'number') {
    return null
  }

  return (free / total) * 100
}

export const getStorageFreePct = (deviceInfo?: StorageFields | null) => {
  const freePct = getStorageFreePctRaw(deviceInfo)

  return freePct === null ? null : Math.round(freePct)
}

export const getStorageUsedGB = (deviceInfo?: StorageFields | null) => {
  const total = deviceInfo?.storageTotalMB
  const free = deviceInfo?.storageFreeMB

  if (typeof total !== 'number' || typeof free !== 'number') {
    return null
  }

  return Math.round((total - free) / MB_PER_GB)
}

export const getStorageTotalGB = (deviceInfo?: StorageFields | null) => {
  const total = deviceInfo?.storageTotalMB

  return typeof total === 'number' ? Math.round(total / MB_PER_GB) : null
}

export const getStorageFreeGB = (deviceInfo?: StorageFields | null) => {
  const free = deviceInfo?.storageFreeMB

  return typeof free === 'number' ? Math.round(free / MB_PER_GB) : null
}

export const getBatteryAgeMonths = (ageDays?: number | null) =>
  typeof ageDays === 'number' ? Math.round(ageDays / DAYS_PER_MONTH) : null

/**
 * Per PT-3633's warning logic table: driven by the health score threshold,
 * not by battery capacity. The ticket's `health_status` field (which would
 * label this straight from the device's own report) isn't exposed by the
 * backend yet, so the score threshold is the only signal we have.
 */
export const isBatteryHealthWarning = (battery?: BatteryFields | null) =>
  typeof battery?.healthScore === 'number' &&
  battery.healthScore < BATTERY_HEALTH_SCORE_WARNING_THRESHOLD

export const isStorageWarning = (deviceInfo?: StorageFields | null) => {
  const freePct = getStorageFreePctRaw(deviceInfo)

  return (
    typeof freePct === 'number' && freePct <= STORAGE_FREE_PCT_WARNING_THRESHOLD
  )
}

export const isComplianceWarning = (
  deviceInfo?: Pick<DeviceHealthSignals, 'complianceState'> | null,
) => {
  const complianceState = deviceInfo?.complianceState

  return (
    !!complianceState &&
    normalizeComplianceState(complianceState) === 'noncompliant'
  )
}

export const getComplianceStateTranslationId = (complianceState: string) => {
  const normalized = normalizeComplianceState(complianceState)
  const translationId =
    normalized in COMPLIANCE_STATE_TRANSLATION_IDS
      ? COMPLIANCE_STATE_TRANSLATION_IDS[
          normalized as keyof typeof COMPLIANCE_STATE_TRANSLATION_IDS
        ]
      : undefined

  if (!translationId) {
    Sentry.captureException(
      new Error(
        `getComplianceStateTranslationId: unknown complianceState returned from the API: ${complianceState}`,
      ),
    )

    return COMPLIANCE_STATE_TRANSLATION_IDS.unknown
  }

  return translationId
}

export const isEncryptionWarning = (
  deviceInfo?: Pick<DeviceHealthSignals, 'isEncrypted'> | null,
) => deviceInfo?.isEncrypted === false

export const isSyncWarning = (
  deviceInfo?: Pick<DeviceHealthSignals, 'lastSyncedAt'> | null,
) => {
  if (!deviceInfo?.lastSyncedAt) {
    return false
  }

  const daysSinceLastSync =
    (Date.now() - new Date(deviceInfo.lastSyncedAt).getTime()) /
    (24 * 60 * 60 * 1000)

  return daysSinceLastSync > SYNC_WARNING_DAYS
}

export const getOverallDeviceHealthStatus = (
  deviceInfo?: DeviceHealthSignals | null,
): DeviceHealthStatus => {
  const hasWarning =
    isBatteryHealthWarning(deviceInfo?.battery) ||
    isStorageWarning(deviceInfo) ||
    isComplianceWarning(deviceInfo) ||
    isEncryptionWarning(deviceInfo) ||
    isSyncWarning(deviceInfo)

  return hasWarning ? DeviceHealthStatus.Attention : DeviceHealthStatus.Healthy
}
